/**
 * Navigation configuration for the Fleet Management application.
 *
 * This file defines the structure and configuration for the main navigation sidebar,
 * including hierarchical menu items, role-based access control, and path handling.
 */

import { IconBuilding, IconCloudComputing, IconDatabase, IconGauge, IconHome, IconKey, IconServer, IconUsers } from '@tabler/icons-react';
import { UserRole } from "@shared-types/auth.types.ts";
import { StudyIcon, UserIcon } from "@/assets/CadwellIcons.tsx";

/**
 * Represents a navigation item in the sidebar menu.
 */
export interface NavItem {
  /** Icon component to display for this navigation item */
  icon: React.ReactNode;
  /** Display label for the navigation item */
  label: string;
  /** Optional path to navigate to when clicked. If not provided, item acts as a section header */
  path?: string;
  /** Optional child navigation items for creating hierarchical menus */
  children?: NavItem[];
  /** Optional role requirement - item only shows if user has this role or higher */
  requiredRole?: UserRole;
}

/**
 * Main navigation structure for the Fleet Management application.
 */
export const navigationItems: NavItem[] = [
  // Main dashboard - accessible to all users
  {
    icon: <IconHome size={16}/>,
    label: "Home",
    path: "/"
  },

  // Customers - all customer-related pages and assigned systems
  {
    icon: <UserIcon size={16}/>,
    label: "Customers",
    path: "/customers"
  },

  // Unassigned Systems - systems without customer assignment
  {
    icon: <StudyIcon size={16}/>,
    label: "Unassigned Systems",
    path: "/systems/unassigned",
    // Only Support and Admin users can see unassigned systems
    requiredRole: UserRole.Support
  },

  // Administration section - restricted to Admin users only
  {
    icon: <IconBuilding size={16}/>,
    label: "Administration",
    requiredRole: UserRole.Admin,
    children: [
      {
        icon: <IconGauge size={16}/>,
        label: "Control Center",
        path: "/admin"
      },
      {
        icon: <IconKey size={16}/>,
        label: "License Viewer",
        path: "/admin/license-viewer"
      },
      {
        icon: <IconCloudComputing size={16}/>,
        label: "Deployment Center",
        path: "/admin/deployment-center"
      },
      {
        icon: <IconDatabase size={16}/>,
        label: "Data Sync",
        path: "/admin/customer-imports"
      },
      {
        icon: <IconUsers size={16}/>,
        label: "User Management",
        path: "/admin/manage-users"
      },
      {
        icon: <IconServer size={16}/>,
        label: "Systems",
        path: "/admin/systems"
      },
    ]
  }
];